import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Input,
  List,
  ListItem,
  ListIcon,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { getPendingDeleteSummary, confirmAllPendingDeletes } from "../../../api/clientsApi";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

/**
 * Confirmation modal for approving every Excel-initiated pending company
 * deletion at once. Fetches the aggregated impact across all pending
 * companies, lists what will be removed, and requires the user to type the
 * exact confirmation phrase (localized: EN "delete all permanently" /
 * FR "tout supprimer définitivement") before the destructive bulk action is
 * enabled. Mirrors {@link DeleteCompanyConfirmModal} but operates on the whole
 * pending set rather than a single company.
 */
const BulkApproveDeletionsModal = ({ isOpen, onClose, buId, onSuccess }) => {
  const { t } = useTranslation("portal");
  const toast = useToast();

  const expectedPhrase = t("pilotClients.list.bulkDeleteConfirm.phrase");

  const [summary, setSummary] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [impactError, setImpactError] = useState(false);
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setSummary(null);
    setImpactError(false);
    setTyped("");
    setLoadingImpact(true);
    getPendingDeleteSummary({ buId: buId || undefined })
      .then((data) => {
        if (active) setSummary(data || null);
      })
      .catch(() => {
        if (active) setImpactError(true);
      })
      .finally(() => {
        if (active) setLoadingImpact(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen, buId]);

  const clientCount = summary?.clientCount || 0;
  const impact = summary?.impact || null;

  const canConfirm =
    clientCount > 0 &&
    typed.trim().toLowerCase() === expectedPhrase.toLowerCase() &&
    !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await confirmAllPendingDeletes(typed.trim(), {
        buId: buId || undefined,
      });
      toast({
        title: t("pilotClients.list.toasts.bulkDeleteSuccess", {
          count: res?.deleted ?? clientCount,
        }),
        status: "success",
      });
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast({
        title: t("pilotClients.list.toasts.bulkDeleteFailedTitle"),
        description:
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          t("pilotClients.list.toasts.bulkDeleteFailedDescription"),
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const impactRows = [];
  if (impact) {
    if (impact.needs) impactRows.push(t("pilotClients.list.bulkDeleteConfirm.needs", { count: impact.needs }));
    if (impact.matches) impactRows.push(t("pilotClients.list.bulkDeleteConfirm.matches", { count: impact.matches }));
    if (impact.contacts) impactRows.push(t("pilotClients.list.bulkDeleteConfirm.contacts", { count: impact.contacts }));
    if (impact.comments) impactRows.push(t("pilotClients.list.bulkDeleteConfirm.comments", { count: impact.comments }));
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {t("pilotClients.list.bulkDeleteConfirm.title")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loadingImpact && (
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <Spinner size="sm" />
              <Text sx={{ marginBottom: "0 !important" }}>
                {t("pilotClients.list.bulkDeleteConfirm.loadingImpact")}
              </Text>
            </Box>
          )}

          {impactError && (
            <Text color="portal.danger" mb={4}>
              {t("pilotClients.list.bulkDeleteConfirm.impactError")}
            </Text>
          )}

          {!loadingImpact && !impactError && clientCount === 0 && (
            <Text mb={4}>
              {t("pilotClients.list.bulkDeleteConfirm.empty")}
            </Text>
          )}

          {!loadingImpact && !impactError && clientCount > 0 && (
            <>
              <Text mb={4}>
                {t("pilotClients.list.bulkDeleteConfirm.intro", { count: clientCount })}
              </Text>
              <Box mb={4}>
                <Text fontWeight="600" mb={2}>
                  {t("pilotClients.list.bulkDeleteConfirm.summaryHeading")}
                </Text>
                <List spacing={1}>
                  <ListItem>
                    <ListIcon as={WarningTwoIcon} color="portal.danger" />
                    {t("pilotClients.list.bulkDeleteConfirm.companies", { count: clientCount })}
                  </ListItem>
                  {impactRows.map((label) => (
                    <ListItem key={label}>
                      <ListIcon as={WarningTwoIcon} color="portal.danger" />
                      {label}
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Text mb={2}>
                {t("pilotClients.list.bulkDeleteConfirm.prompt", { phrase: expectedPhrase })}
              </Text>
              <Input
                data-testid="bulk-delete-confirm-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={expectedPhrase}
                autoFocus
              />
            </>
          )}
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
            {t("pilotClients.list.bulkDeleteConfirm.cancel")}
          </Button>
          <Button
            colorScheme="red"
            data-testid="bulk-delete-confirm-submit"
            onClick={handleConfirm}
            isDisabled={!canConfirm}
            isLoading={submitting}
          >
            {t("pilotClients.list.bulkDeleteConfirm.confirm")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkApproveDeletionsModal;
